export interface NextCrewNumber {
  crew_number_int: number;
  crew_number: string;
  crew_number_digits: number;
  counter_last_assigned: number | null;
}

export interface CrewNumberCheck {
  status: 'available' | 'reserved' | 'taken';
  crew_number: string;
  crew_id: string | null;
  crew_name: string | null;
  crew_status: string | null;
  reserved_reason: string | null;
  counter_last_assigned: number | null;
}

export interface CrewNumberDescription {
  tone: 'ok' | 'warn' | 'error';
  text: string;
}

/**
 * Meme normalisation que le transform Zod de crewNumberSchema et que la RPC :
 * seuls les chiffres comptent ("EQ1717" -> 1717). Null si aucun chiffre.
 */
export function parseCrewNumberInt(raw: string | undefined): number | null {
  const digits = raw?.replace(/\D/g, '') ?? '';
  if (!digits) return null;
  const parsed = Number.parseInt(digits, 10);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}

/** Codes serveur pour lesquels la saisie manuelle reste acceptee sans controle. */
export const CREW_NUMBER_ASSIST_UNAVAILABLE_CODES = [
  'missing_sandra_config',
  'missing_product_start_date',
] as const;

export class CrewNumberError extends Error {
  readonly code: string;

  constructor(code: string) {
    super(crewNumberErrorMessage(code));
    this.name = 'CrewNumberError';
    this.code = code;
  }
}

export function isCrewNumberAssistUnavailable(error: unknown): boolean {
  return (
    error instanceof CrewNumberError &&
    (CREW_NUMBER_ASSIST_UNAVAILABLE_CODES as readonly string[]).includes(error.code)
  );
}

export function crewNumberErrorMessage(message: string): string {
  const messages: Record<string, string> = {
    access_denied: 'Vous n’avez pas les droits nécessaires.',
    authentication_required: 'Votre session a expiré. Reconnectez-vous.',
    assigned_to_forbidden: 'Vous ne pouvez pas assigner cet équipage à un autre utilisateur.',
    product_not_found: 'Produit introuvable.',
    missing_product_start_date:
      'Produit sans date de début : saisissez le numéro manuellement (pas de vérification possible).',
    missing_sandra_config:
      'Produit sans configuration de numérotation : saisissez le numéro manuellement (pas de vérification possible).',
    invalid_crew_number: 'Le numéro d’équipage est invalide.',
    crew_number_taken: 'Ce numéro vient d’être attribué à un autre équipage.',
    crew_number_reserved: 'Ce numéro est réservé et doit être confirmé.',
    crew_number_search_exhausted: 'Aucun numéro disponible trouvé dans la plage suivante.',
    crew_number_out_of_range: 'Ce numéro dépasse le format autorisé pour ce produit.',
  };
  return messages[message] ?? message;
}

export function describeCrewNumberCheck(
  check: CrewNumberCheck,
  requestedInt: number
): CrewNumberDescription {
  const isOldNumber =
    check.counter_last_assigned !== null && requestedInt <= check.counter_last_assigned;
  const oldNumberSuffix = isOldNumber
    ? ` (ancien numéro, compteur courant : ${check.counter_last_assigned})`
    : '';

  if (check.status === 'taken') {
    const crewLabel = check.crew_name?.trim() || check.crew_number;
    return {
      tone: 'error',
      text: `Déjà attribué à l’équipage « ${crewLabel} »${oldNumberSuffix}`,
    };
  }

  if (check.status === 'reserved') {
    return {
      tone: 'warn',
      text: `Numéro réservé${check.reserved_reason ? ` : ${check.reserved_reason}` : ''}${oldNumberSuffix}`,
    };
  }

  return {
    tone: isOldNumber ? 'warn' : 'ok',
    text: `Numéro disponible${oldNumberSuffix}`,
  };
}
